import { useState } from 'react';
import { Modal, Table, Tag, Button, Space, Typography, Alert, App } from 'antd';
import { ImportOutlined, CheckCircleOutlined, LinkOutlined, PlusCircleOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { TradevestAssetPreview, TradevestImportResult, AssetCategory } from '@mygate/common';
import { assetManagementService } from '@/services/asset-management';

const { Text } = Typography;

interface TradevestImportModalProps {
  visible: boolean;
  onClose: () => void;
  onImportComplete: () => void;
}

export function TradevestImportModal({ visible, onClose, onImportComplete }: TradevestImportModalProps) {
  const { message } = App.useApp();
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [previews, setPreviews] = useState<TradevestAssetPreview[]>([]);
  const [selectedSymbols, setSelectedSymbols] = useState<string[]>([]);
  const [results, setResults] = useState<TradevestImportResult[] | null>(null);
  const [previewLoaded, setPreviewLoaded] = useState(false);

  const loadPreview = async () => {
    setLoading(true);
    try {
      const response = await assetManagementService.previewTradevestImport();
      if (response.data) {
        setPreviews(response.data.assets);
        setPreviewLoaded(true);
        // Auto-select importable assets (not already linked)
        const importable = response.data.assets.filter(a => !a.hasTradevestLink).map(a => a.symbol);
        setSelectedSymbols(importable);
      }
    } catch {
      message.error('Failed to load Tradevest assets preview');
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (selectedSymbols.length === 0) {
      message.warning('No assets selected for import');
      return;
    }
    setImporting(true);
    try {
      const response = await assetManagementService.executeTradevestImport(selectedSymbols);
      if (response.data) {
        setResults(response.data.results);
        const { assetsCreated, linksCreated, skipped, errors } = response.data.summary;
        message.success(
          `Import complete: ${assetsCreated} created, ${linksCreated} linked, ${skipped} skipped, ${errors} errors`,
        );
        onImportComplete();
      }
    } catch {
      message.error('Failed to import assets from Tradevest');
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    setPreviews([]);
    setSelectedSymbols([]);
    setResults(null);
    setPreviewLoaded(false);
    onClose();
  };

  const getStatusTag = (preview: TradevestAssetPreview) => {
    if (preview.hasTradevestLink) {
      return (
        <Tag color="green" icon={<CheckCircleOutlined />}>
          Already Linked
        </Tag>
      );
    }
    if (preview.existsInDb) {
      return (
        <Tag color="blue" icon={<LinkOutlined />}>
          Exists, No Link
        </Tag>
      );
    }
    return (
      <Tag color="orange" icon={<PlusCircleOutlined />}>
        New
      </Tag>
    );
  };

  const getResultTag = (result: TradevestImportResult) => {
    switch (result.action) {
      case 'created_asset':
        return <Tag color="green">Created Asset + Link</Tag>;
      case 'created_link':
        return <Tag color="blue">Created Link</Tag>;
      case 'skipped':
        return <Tag color="default">Skipped</Tag>;
      case 'error':
        return <Tag color="red">Error: {result.error}</Tag>;
    }
  };

  const previewColumns: ColumnsType<TradevestAssetPreview> = [
    {
      title: 'Symbol',
      dataIndex: 'symbol',
      key: 'symbol',
      width: 100,
      render: symbol => <Text strong>{symbol}</Text>,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: 'Trading Precision',
      dataIndex: 'decimals',
      key: 'decimals',
      width: 130,
      align: 'center',
      render: (val: number) => <Text type="secondary">{val} dp</Text>,
    },
    {
      title: 'Type',
      dataIndex: 'assetType',
      key: 'assetType',
      width: 170,
      render: (type: string, record) =>
        record.category === AssetCategory.RWA ? (
          <Tag color="purple">{type} → RWA</Tag>
        ) : (
          <Text type="secondary">{type}</Text>
        ),
    },
    {
      title: 'Status',
      key: 'status',
      width: 160,
      render: (_, record) => getStatusTag(record),
    },
  ];

  const resultColumns: ColumnsType<TradevestImportResult> = [
    {
      title: 'Symbol',
      dataIndex: 'symbol',
      key: 'symbol',
      width: 100,
      render: symbol => <Text strong>{symbol}</Text>,
    },
    {
      title: 'Result',
      key: 'action',
      render: (_, record) => getResultTag(record),
    },
  ];

  const newAssets = previews.filter(a => !a.existsInDb);
  const existsNoLink = previews.filter(a => a.existsInDb && !a.hasTradevestLink);
  const alreadyLinked = previews.filter(a => a.hasTradevestLink);

  return (
    <Modal
      title="Import Assets from Tradevest"
      open={visible}
      onCancel={handleClose}
      width={800}
      footer={
        results ? (
          <Button onClick={handleClose}>Close</Button>
        ) : (
          <Space>
            <Button onClick={handleClose}>Cancel</Button>
            {!previewLoaded ? (
              <Button type="primary" icon={<ImportOutlined />} loading={loading} onClick={loadPreview}>
                Load Preview
              </Button>
            ) : (
              <Button
                type="primary"
                icon={<ImportOutlined />}
                loading={importing}
                onClick={handleImport}
                disabled={selectedSymbols.length === 0}
              >
                Import {selectedSymbols.length} Asset{selectedSymbols.length !== 1 ? 's' : ''}
              </Button>
            )}
          </Space>
        )
      }
    >
      {results ? (
        <Table columns={resultColumns} dataSource={results} rowKey="symbol" pagination={false} size="small" />
      ) : previewLoaded ? (
        <>
          <Space style={{ marginBottom: 16 }}>
            <Tag color="orange">{newAssets.length} New</Tag>
            <Tag color="blue">{existsNoLink.length} Need Link</Tag>
            <Tag color="green">{alreadyLinked.length} Already Linked</Tag>
          </Space>
          <Alert
            message="On-chain decimals must be set manually"
            description="Tradevest 'Trading Precision' is NOT on-chain token decimals. Newly imported assets default to 18 decimals. You must verify and set the correct on-chain decimals (e.g. 6 for USDT/USDC, 18 for most ERC-20) on the asset detail page before activating."
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
          {alreadyLinked.length > 0 && (
            <Alert
              message={`${alreadyLinked.length} asset(s) are already linked to Tradevest and will be skipped.`}
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}
          <Table
            columns={previewColumns}
            dataSource={previews}
            rowKey="symbol"
            pagination={false}
            size="small"
            scroll={{ y: 400 }}
            rowSelection={{
              selectedRowKeys: selectedSymbols,
              onChange: keys => setSelectedSymbols(keys as string[]),
              getCheckboxProps: record => ({
                disabled: record.hasTradevestLink,
              }),
            }}
          />
        </>
      ) : (
        <Alert
          message="Click 'Load Preview' to fetch available assets from Tradevest and compare with existing database records."
          type="info"
          showIcon
        />
      )}
    </Modal>
  );
}
